import { yupResolver } from '@hookform/resolvers/yup';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Box, FormHelperText, Grid, Stack, styled } from '@mui/material';
import { isEmpty, keys } from 'lodash-es';
import { useState } from 'react';
import { Controller, FieldValues, FormProvider, useForm } from 'react-hook-form';
import { ICandidate, importToVMS, newMappingEntity, uploadFileToS3 } from 'wisestep-api';
import {
  ControlledTextInput,
  Item,
  Label,
  LocationInfo,
  WsColors,
  WsFileUpload,
  WsTypography,
  WsTypoVariants,
  ControlledSocialLinkInput,
  WsDatePicker,
  ControlledWsButtonGroup,
  WsSelect,
  ControlledTextArea,
  WsButton,
} from 'wisestep-base';

import EducationField from '../../../../../components/EducationField';
import ExperienceField from '../../../../../components/ExperienceField';
import MonthAndYearField from '../../../../../components/MonthAndYearField';
import SkillsField from '../../../../../components/SkillsField';
import { success, failed, warning } from '../../../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../../../utils/NotificationStore';
import ActionFooter from '../../../../conversationWidget/components/ActionFooter';
import { trackCV, downloadCV, ITrackCvPages } from '../../../../conversationWidget/utils/cvHelper';
import { booleanList, flsaList, worldLanguages } from '../../helper/menuList';
import { prowAndProFormDefaultValues } from '../../validator/defaultValue';
import { prowAndProSchema } from '../../validator/schema';
import { setProwAndProCandidate } from '../../validator/setCandidate';

import VMSAccordion from './VMSAccordion';

const GridItem = styled(Item)(() => ({
  paddingTop: 0,
  paddingBottom: '10px',
}));

const ProAndProUpload = ({ candidateDetail }: { candidateDetail: ICandidate }) => {
  const [candidate, setCandidate] = useState(candidateDetail);
  const formOptions = {
    resolver: yupResolver(prowAndProSchema),
    defaultValues: prowAndProFormDefaultValues(candidateDetail),
  };
  const methods = useForm(formOptions);
  const { handleSubmit, control } = methods;
  const setNotification = useNotificationStore((state) => state.setNotification);

  const submitHandler = async (data: FieldValues) => {
    if (!isEmpty(candidateDetail?.resumeLocation)) {
      if (!isEmpty(candidateDetail.currentLocation)) {
        try {
          const newCandidate = setProwAndProCandidate(candidateDetail, data);
          const req = await importToVMS(newCandidate);
          if (req.data === 'SUCCESS') {
            setNotification(success('Candidate Submitted to VMS Succesfully'));
            setTimeout(() => window.location.reload, 400);
          } else {
            setNotification(failed('Something went wrong. Please try later'));
          }
        } catch (e) {
          console.log(e);
          setNotification(failed('Something went wrong. Please try later'));
        }
      } else {
        setNotification(warning('Please enter candidate location'));
      }
    } else {
      setNotification(warning('Please upload resume'));
    }
  };

  const onUpload = async (file: File[], field: string) => {
    const url = await uploadFileToS3(file[0]);
    if (field === 'resume') {
      const alteredCandidate = candidate ?? newMappingEntity();
      alteredCandidate.resumeLocation = url;
      alteredCandidate.resumeFileOriginalName = file[0].name;
      alteredCandidate.resumeUploadedOn = new Date();
      const res = {
        candidateMappingId: '',
        email: '',
        gitHubLink: '',
        id: '',
        linkedInLink: '',
        phone: '',
        pinCode: '',
        resumeFileOriginalName: file[0].name,
        resumeLocation: url,
        resumeUploadedOn: new Date(),
      };
      alteredCandidate.mappedCandidateResumes = [res];
      setCandidate({ ...alteredCandidate });
    }
  };

  const resumeAction = (action: string, url: string | null) => {
    switch (action) {
      case 'view':
        !isEmpty(url) && trackCV('view', url, ITrackCvPages.WISESTEP);
        break;
      case 'download':
        if (!isEmpty(url)) {
          trackCV('download', url, ITrackCvPages.WISESTEP);
          url && downloadCV(url);
        }
        break;
      case 'removeCandidate':
        if (candidate) {
          const alteredCandidate = { ...candidate };
          alteredCandidate.resumeFileOriginalName = '';
          alteredCandidate.resumeLocation = '';
          alteredCandidate.resumeUploadedOn = null;
          setCandidate({ ...alteredCandidate });
        }
        break;
      case 'removeOtherDocument':
        if (candidate) {
          const alteredCandidate = { ...candidate };
          alteredCandidate.otherDocuments = alteredCandidate?.otherDocuments?.filter(
            (document) => document.documentLocation != url
          );
          setCandidate({ ...alteredCandidate });
        }
        break;
    }
  };

  const onError = (e) => {
    console.log(e);
    setNotification(warning('Please enter required fields'));
  };

  return (
    <FormProvider {...methods}>
      <Box sx={{ width: '100%' }} component="form" onSubmit={handleSubmit(submitHandler, onError)}>
        <Grid container sx={{ maxHeight: '90vh', overflowY: 'auto' }}>
          <VMSAccordion header="Personal Info">
            <GridItem>
              <ControlledTextInput
                name="name"
                labelText="Name *"
                fullWidth
                id="candidate_name"
                placeholder="Enter Candidate Name"
                variant="outlined"
                autoComplete="candidate_name"
                required
              />
            </GridItem>
            <GridItem>
              <ControlledTextInput
                name="email"
                labelText="Email address *"
                type="email"
                fullWidth
                id="candidate_email"
                placeholder="Enter Candidate Email Address"
                variant="outlined"
                autoComplete="candidate_email"
                required
              />
            </GridItem>
            <GridItem>
              <ControlledTextInput
                name="phone"
                labelText="Phone Number *"
                fullWidth
                id="candidate_phone"
                placeholder="Enter Candidate Phone Number"
                variant="outlined"
                autoComplete="candidate_phone"
                required
              />
            </GridItem>
            <GridItem>
              <Label>CV *</Label>
              {candidate?.resumeFileOriginalName ? (
                <Stack direction="row" spacing={2} mb={2}>
                  <WsTypography variant={WsTypoVariants.BODY1}>
                    {candidate?.resumeFileOriginalName ?? ''}
                  </WsTypography>
                  <DownloadIcon
                    onClick={() =>
                      resumeAction && resumeAction('download', candidate.resumeLocation)
                    }
                    sx={{ color: WsColors.TheBlues500, fontSize: '24px', cursor: 'pointer' }}
                  />
                  <VisibilityIcon
                    onClick={() => resumeAction && resumeAction('view', candidate.resumeLocation)}
                    sx={{ color: WsColors.TheBlues500, cursor: 'pointer' }}
                  />
                  <CloseIcon
                    onClick={() =>
                      resumeAction && resumeAction('removeCandidate', candidate.resumeLocation)
                    }
                    sx={{ color: WsColors.Red500, cursor: 'pointer' }}
                  />
                </Stack>
              ) : (
                <WsFileUpload
                  multiple={false}
                  onUpload={(file: File[]) => onUpload && onUpload(file, 'resume')}
                  name="resume"
                  placeholderText=" "
                  bgRequired={false}
                  buttonText={'Upload File'}
                />
              )}
            </GridItem>
            <GridItem>
              <ControlledSocialLinkInput name="socialLinks" labelText="Social Link *" />
            </GridItem>
            <LocationInfo showTitle={false} />
            <GridItem>
              <Label>Candidate Date of Birth *</Label>
              <Controller
                name="dob"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <MonthAndYearField initialValue={value} onInputChange={onChange} />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
              {/* <WsDatePicker name="dob" label="" disableFuture={false} disabled={false} /> */}
            </GridItem>
            <GridItem>
              <ControlledTextInput
                name="portfolio"
                labelText="Portfolio"
                fullWidth
                id="candidate_portfolio"
                placeholder=""
                variant="outlined"
                autoComplete="candidate_portfolio"
              />
            </GridItem>
          </VMSAccordion>
          <VMSAccordion header="Skills *">
            <GridItem>
              <Controller
                name="candidateSkills"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <SkillsField
                      initialValue={!isEmpty(value) ? value : [{ type: '', messengerId: '' }]}
                      onInputChange={onChange}
                      labelText=""
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
            </GridItem>
          </VMSAccordion>
          <VMSAccordion header="Experience *">
            <GridItem>
              <Controller
                name="experience"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <ExperienceField
                      initialValue={!isEmpty(value) ? value : null}
                      onInputChange={onChange}
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
            </GridItem>
          </VMSAccordion>
          <VMSAccordion header="Education *">
            <GridItem>
              <Controller
                name="education"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <EducationField
                      initialValue={!isEmpty(value) ? value : null}
                      onInputChange={onChange}
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
            </GridItem>
          </VMSAccordion>
          <VMSAccordion header="Military Service">
            <GridItem>
              <Controller
                name="militaryService"
                control={control}
                render={({ field: { onChange, value }, fieldState: { error } }) => (
                  <>
                    <ExperienceField
                      initialValue={!isEmpty(value) ? value : null}
                      onInputChange={onChange}
                    />
                    {error && <FormHelperText error>{error.message}</FormHelperText>}
                  </>
                )}
              />
            </GridItem>
          </VMSAccordion>
          <VMSAccordion header="Languages *">
            <GridItem>
              <WsSelect name="language" label="Select language *" selectArray={worldLanguages} />
            </GridItem>
          </VMSAccordion>
          <VMSAccordion header="Additional Information">
            <Grid container>
              <Grid item xs={12}>
                <GridItem>
                  <ControlledTextInput
                    name="ssnNumber"
                    labelText="SSN Number *"
                    type="number"
                    fullWidth
                    id="candidate_ssnNumber"
                    placeholder="Enter SSN Number"
                    variant="outlined"
                    autoComplete="candidate_ssnNumber"
                    required={true}
                  />
                </GridItem>
                <GridItem>
                  <ControlledTextInput
                    name="payRate"
                    labelText="Pay Rate *"
                    fullWidth
                    id="candidate_pay_rate"
                    placeholder="Enter pay rate"
                    variant="outlined"
                    autoComplete="candidate_pay_rate"
                  />
                </GridItem>
                <GridItem>
                  <Label>Available From *</Label>
                  <WsDatePicker
                    name="availableFrom"
                    label=""
                    disableFuture={false}
                    disabled={false}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={6}>
                <GridItem>
                  <ControlledWsButtonGroup
                    name="isPreviousEmployee"
                    inputTitle="Previous Employee *"
                    buttonsList={keys(booleanList)}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={6}>
                <GridItem>
                  <ControlledWsButtonGroup
                    name="providedServiceThroughThirdParty"
                    inputTitle="Previously Provided Service via 3rd Party *"
                    buttonsList={keys(booleanList)}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={6}>
                <GridItem>
                  <ControlledWsButtonGroup
                    name="subContractedWorker"
                    inputTitle="Sub Contracted Worker *"
                    buttonsList={keys(booleanList)}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={6}>
                <GridItem>
                  <ControlledWsButtonGroup
                    name="isH1B"
                    inputTitle="H1B *"
                    buttonsList={keys(booleanList)}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={6}>
                <GridItem>
                  <WsSelect
                    name="flsaClassification"
                    label="Supplier FLSA Classification *"
                    selectArray={flsaList}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={12}>
                <GridItem>
                  <ControlledTextArea
                    name="supplierNotes"
                    labelText="Supplier notes"
                    showMandatorySymbol={true}
                  />
                </GridItem>
              </Grid>
              <Grid item xs={12}>
                <GridItem>
                  <ControlledTextArea
                    name="summary"
                    labelText="Summary"
                    showMandatorySymbol={true}
                  />
                </GridItem>
              </Grid>
            </Grid>
          </VMSAccordion>
          <Box mt={10}>
            <GridItem></GridItem>
          </Box>
          <ActionFooter>
            <Grid item container xs={6} justifyContent="flex-end">
              <Box mr={3}>
                <Stack direction="row" spacing={2}>
                  <WsButton variant="outlined" type="submit">
                    Save
                  </WsButton>
                </Stack>
              </Box>
            </Grid>
          </ActionFooter>
        </Grid>
      </Box>
    </FormProvider>
  );
};

export default ProAndProUpload;
